/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useEffect, useState } from 'react';
import { getPendingSessions } from '@/modules/venta/offline/offlineSessionQueue';
import { queueCount } from '@/modules/venta/offline/offlineSaleQueue';

export interface OfflinePendingWork {
  /** True when unsynced offline POS work exists in IndexedDB. */
  pending: boolean;
  /** False until the first async IndexedDB read lands (so callers can wait, not flash). */
  hydrated: boolean;
}

/**
 * Reports whether there is unsynced offline POS work in IndexedDB: any unsynced till
 * session (OPEN or CLOSED-awaiting-sync) or any queued sale.
 *
 * Used by `ProtectedRoute` to keep a cashier in the offline POS after their JWT expires
 * *while offline* — they can finish the shift instead of being stranded on an unreachable
 * `/auth/login`. Re-auth is forced on reconnect (the guard stops tolerating once online),
 * and the queued work — which `logout()` deliberately leaves untouched — syncs afterwards.
 */
export function useOfflinePendingWork(): OfflinePendingWork {
  const [pending, setPending] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [sessions, sales] = await Promise.all([getPendingSessions(), queueCount()]);
        if (!cancelled) setPending(sessions.length > 0 || sales > 0);
      } catch {
        if (!cancelled) setPending(false);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { pending, hydrated };
}
